import type { GroupedApprovals } from '../../queries/approvals/approvalsUtils';
import type { Approval, ApprovalCardRequest } from '../../repositories/approvalRepository';
import type { ZustandStore } from '../store.type';
import type { NamedSet } from 'zustand/middleware';

export const sortApprovalsOptions = {
	mostRecent: 'desc',
	requester: 'asc',
	lowToHigh: 'asc',
	highToLow: 'desc',
} as const;

export type SortApprovalsKey = keyof typeof sortApprovalsOptions;
type OrderApprovalsKey = (typeof sortApprovalsOptions)[SortApprovalsKey];

export const defaultApprovalSort = { mostRecent: 'desc' as OrderApprovalsKey };

export type SingleSortOption = {
	[K in SortApprovalsKey]: Record<K, OrderApprovalsKey>;
}[SortApprovalsKey];

type ApprovalsState = {
	approvals: {
		searchValue: string;
		sortValue: SingleSortOption;
		list: GroupedApprovals;
		openForwardRequestAside: boolean;
		currentApprovalItem: Approval | null;
		approvalRequest: ApprovalCardRequest;
		showConversationButtons: boolean;
		showCreateCardButton: boolean;
		viewApprovalConversationRequestId: string | undefined;
		showCreateFwdButtons: boolean;
		showNoActiveCardInfo: boolean;
		isForwardRequestButtonDisabled: boolean;
		isCreateCardSuccessful: boolean;
	};
};

type ApprovalsActions = {
	setSearchApprovalsValue: (searchValue: ApprovalsState['approvals']['searchValue']) => void;
	setSortApprovalsValue: (sortValue: ApprovalsState['approvals']['sortValue']) => void;
	setUiApprovals: (list: ApprovalsState['approvals']['list']) => void;
	setOpenForwardRequestAside: (value: ApprovalsState['approvals']['openForwardRequestAside']) => void;
	setCurrentApproverItem: (value: ApprovalsState['approvals']['currentApprovalItem']) => void;
	setApprovalRequest: (approvalRequest: ApprovalsState['approvals']['approvalRequest']) => void;
	setShowConversationButtons: (approvalRequest: ApprovalsState['approvals']['showConversationButtons']) => void;
	shouldShowCreateCardButton: (approvalRequest: ApprovalsState['approvals']['showCreateCardButton']) => void;
	setShowCreateFwdButtons: (approvalRequest: ApprovalsState['approvals']['showCreateFwdButtons']) => void;
	setShowNoActiveCardInfo: (approvalRequest: ApprovalsState['approvals']['showNoActiveCardInfo']) => void;
	setViewApprovalConversationRequestId: (
		requestId: ApprovalsState['approvals']['viewApprovalConversationRequestId'],
	) => void;
	setIsForwardRequestButtonDisabled: (
		isForwardRequestButtonDisabled: ApprovalsState['approvals']['isForwardRequestButtonDisabled'],
	) => void;
	setIsCreateCardSuccessful: (isCreateCardSuccessful: ApprovalsState['approvals']['isCreateCardSuccessful']) => void;
};

export type ApprovalsSlice = ApprovalsState & ApprovalsActions;

const ApprovalsInitialState: ApprovalsState = {
	approvals: {
		searchValue: '',
		sortValue: {
			mostRecent: 'desc',
		},
		list: {
			pending: { approvals: [], count: 0 },
			approved: { approvals: [], count: 0 },
			declined: { approvals: [], count: 0 },
			canceled: { approvals: [], count: 0 },
		},
		openForwardRequestAside: false,
		currentApprovalItem: null,
		approvalRequest: {
			cardSourceId: '',
			cardType: '',
			coding: {},
			eventId: '',
			requesterUserId: '',
			requestId: '',
			taskId: '',
			endDate: '',
		},
		showConversationButtons: true,
		showCreateCardButton: true,
		showCreateFwdButtons: true,
		showNoActiveCardInfo: false,
		viewApprovalConversationRequestId: undefined,
		isForwardRequestButtonDisabled: false,
		isCreateCardSuccessful: false,
	},
};

export function approvalsSlice(set: NamedSet<ZustandStore>): ApprovalsSlice {
	return {
		...ApprovalsInitialState,
		setSearchApprovalsValue(searchValue: ApprovalsState['approvals']['searchValue']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.searchValue = searchValue;
					return state;
				},
				false,
				'[setSearchApprovalsValue]',
			);
		},
		setSortApprovalsValue(sortValue: ApprovalsState['approvals']['sortValue']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.sortValue = sortValue;
					return state;
				},
				false,
				'[setSortApprovalsValue]',
			);
		},
		setUiApprovals(list: ApprovalsState['approvals']['list']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.list = list;
					return state;
				},
				false,
				'[setUiApprovals]',
			);
		},
		setOpenForwardRequestAside(value: ApprovalsState['approvals']['openForwardRequestAside']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.openForwardRequestAside = value;
					return state;
				},
				false,
				'[setOpenForwardRequestAside]',
			);
		},
		setCurrentApproverItem(value: ApprovalsState['approvals']['currentApprovalItem']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.currentApprovalItem = value;
					return state;
				},
				false,
				'[setCurrentApproverItem]',
			);
		},
		setApprovalRequest(approvalRequest: ApprovalsState['approvals']['approvalRequest']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.approvalRequest = approvalRequest;
					return state;
				},
				false,
				'[setApprovalRequest]',
			);
		},
		setShowConversationButtons(showConversationButtons: ApprovalsState['approvals']['showConversationButtons']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.showConversationButtons = showConversationButtons;
					return state;
				},
				false,
				'[setShowConversationButtons]',
			);
		},
		shouldShowCreateCardButton(showCreateCardButton: ApprovalsState['approvals']['showCreateCardButton']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.showCreateCardButton = showCreateCardButton;
					return state;
				},
				false,
				'[showCreateCardButton]',
			);
		},
		setShowCreateFwdButtons(showCreateFwdButtons: ApprovalsState['approvals']['showCreateFwdButtons']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.showCreateFwdButtons = showCreateFwdButtons;
					return state;
				},
				false,
				'[showCreateFwdButtons]',
			);
		},
		setShowNoActiveCardInfo(showNoActiveCardInfo: ApprovalsState['approvals']['showNoActiveCardInfo']): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.showNoActiveCardInfo = showNoActiveCardInfo;
					return state;
				},
				false,
				'[showNoActiveCardInfo]',
			);
		},
		setViewApprovalConversationRequestId(
			requestId: ApprovalsState['approvals']['viewApprovalConversationRequestId'],
		): void {
			set(
				(state: ApprovalsState) => {
					state.approvals.viewApprovalConversationRequestId = requestId;
					return state;
				},
				false,
				'[setViewApprovalConversationRequestId]',
			);
		},
		setIsForwardRequestButtonDisabled: (
			isForwardRequestButtonDisabled: ApprovalsState['approvals']['isForwardRequestButtonDisabled'],
		): void => {
			set(
				(state: ApprovalsState) => {
					state.approvals.isForwardRequestButtonDisabled = isForwardRequestButtonDisabled;
					return state;
				},
				false,
				'[setIsForwardRequestButtonDisabled]',
			);
		},
		setIsCreateCardSuccessful: (
			isCreateCardSuccessful: ApprovalsState['approvals']['isCreateCardSuccessful'],
		): void => {
			set(
				(state: ApprovalsState) => {
					state.approvals.isCreateCardSuccessful = isCreateCardSuccessful;
					return state;
				},
				false,
				'[setIsCreateCardSuccessful]',
			);
		},
	};
}
